import React, { useMemo, useEffect, useCallback, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { generatePath } from 'react-router'
import { useLocation, useSearchParams } from 'react-router-dom'
import styled from 'styled-components'

import { NavDropdown, NavDropdownOption } from 'portal/lib/primitives'
import { useModuleIds, useResponsive } from 'portal/lib/hooks'
import * as ROUTES from 'portal/routes'
import { trackSwitcherSelect } from './trackSwitcherSelect'
import { trackSwitcherOpen } from './trackSwitcherOpen'
import { selectProjectsForSwitcherFactory } from './selectProjectsForSwitcherFactory'
import { projectsGetSaga } from 'portal/store/projects/actions'

export function ProjectSwitcher(props) {
  const dispatch = useDispatch()
  const { accountId, projectId, moduleName } = useModuleIds()
  const [open, setOpen] = useState(false)
  const { isMobile } = useResponsive()

  const { projects } = useSelector(
    useMemo(() => selectProjectsForSwitcherFactory(moduleName), [moduleName])
  )

  useEffect(() => {
    if (accountId) {
      dispatch(projectsGetSaga(accountId))
    }
  }, [accountId, dispatch])

  const { pathname } = useLocation()
  const [searchParams] = useSearchParams()

  const items = useMemo(() => {
    const append = pathname.endsWith('/events') ? '/events' : ''

    const search = searchParams.toString()

    const dynamicRoute = () => {
      const currentURL = pathname.split('/')
      switch (currentURL[currentURL.length - 1]) {
        case 'readings':
          return ROUTES.UTILITIES_READINGS
        case 'meters':
          return ROUTES.UTILITIES_METERS
        case 'material-certifications':
          return ROUTES.ALERTS_MATERIAL_CERTIFICATIONS_TABLE
        case 'quality-assurance':
          return ROUTES.ALERTS_QUALITY_ASSURANCE_TABLE
        case 'submission-issues':
          return ROUTES.ALERTS_SUBMISSION_ISSUES_TABLE
        case 'waste-compliance':
          return ROUTES.ALERTS_WASTE_COMPLIANCE_TABLE
        default:
          return ROUTES.BASE_MODULE_NAV
      }
    }

    return projects
      .map(({ key, label, moduleName }) => ({
        key,
        label,
        enabled: true,
        icon: null,
        selected: key === projectId,
        uri:
          accountId &&
          `${generatePath(dynamicRoute(), {
            accountId,
            projectId: key,
            moduleName
          })}${append}${search ? `?${search}` : ''}`
      }))
      .sort((a, b) =>
        a.label.localeCompare(b.label, 'en', { sensitivity: 'base' })
      )
  }, [accountId, pathname, projectId, projects, searchParams])

  const [selectedItemKey, selectedItem] = useMemo(() => {
    const selectedItem = items.find(item => item.selected) || {}
    const selectedModuleKey = selectedItem.key

    return [selectedModuleKey, selectedItem]
  }, [items])

  const handleSetOpen = useCallback(
    nextOpen => {
      setOpen(nextOpen)

      if (nextOpen && !open) {
        dispatch(trackSwitcherOpen(selectedItemKey))
      }
    },
    [dispatch, open, selectedItemKey]
  )

  const handleClick = useCallback(
    item => {
      setOpen(false)
      setTimeout(() => {
        dispatch(
          trackSwitcherSelect(selectedItemKey, item.key, accountId, projectId)
        )
      }, 0)
    },
    [accountId, dispatch, projectId, selectedItemKey]
  )

  const loaded = !!projectId && !!selectedItemKey

  if (!projectId) {
    return <Container {...props} />
  }

  return (
    <Container {...props} data-testid="project-switcher">
      <NavDropdown
        id="ProjectNavDropdown"
        open={open}
        setOpen={handleSetOpen}
        label={isMobile ? 'Project' : selectedItem.label}
        title={selectedItem.label}
        disabled={items.length <= 1}
        loading={!loaded}
      >
        {items.map(item => (
          <NavDropdownOption
            id={`NavigateTo_${item.label}`}
            data-testid={`NavigateTo_${item.label}`}
            key={item.key}
            Icon={item.icon}
            label={item.label}
            uri={item.uri}
            onClick={handleClick}
            onClickPassbackValue={item}
            selected={item.selected}
          />
        ))}
      </NavDropdown>
    </Container>
  )
}

const Container = styled.div`
  display: flex;
  flex: 0 0 auto;
`
